import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const AUTH_STORAGE_KEY = 'tierlist_admin_auth'
const AUTH_EXPIRY_HOURS = 8 // Authentication expires after 8 hours

/**
 * Admin Authentication Store
 * Manages admin password authentication state with localStorage persistence
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const authTimestamp = ref(null)
  const isLoading = ref(false)
  const lastError = ref(null)
  const adminPassword = ref(null) // Store password temporarily for API calls (in memory only, not persisted)

  // Getters
  const isAuthValid = computed(() => {
    if (!isAuthenticated.value || !authTimestamp.value) {
      return false
    }
    
    const now = new Date().getTime()
    const authTime = new Date(authTimestamp.value).getTime()
    const expiryTime = authTime + (AUTH_EXPIRY_HOURS * 60 * 60 * 1000)
    
    return now < expiryTime
  })

  const needsAuthentication = computed(() => {
    return !isAuthValid.value
  })

  const timeUntilExpiry = computed(() => {
    if (!authTimestamp.value) return 0
    
    const now = new Date().getTime()
    const authTime = new Date(authTimestamp.value).getTime()
    const expiryTime = authTime + (AUTH_EXPIRY_HOURS * 60 * 60 * 1000)
    
    return Math.max(0, expiryTime - now)
  })

  // Actions
  const loadAuthFromStorage = () => {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY)
      if (stored) {
        const authData = JSON.parse(stored)

        // Verify the stored auth is still valid
        const now = new Date().getTime()
        const authTime = new Date(authData.timestamp).getTime()
        const expiryTime = authTime + (AUTH_EXPIRY_HOURS * 60 * 60 * 1000)

        if (now < expiryTime) {
          isAuthenticated.value = true
          authTimestamp.value = authData.timestamp
          adminPassword.value = authData.password // Restore password from localStorage
          return true
        } else {
          // Expired auth, clear it
          clearAuth()
        }
      }
    } catch (error) {
      console.warn('Failed to load auth from storage:', error)
      clearAuth()
    }
    return false
  }

  const saveAuthToStorage = () => {
    try {
      const authData = {
        authenticated: true,
        timestamp: authTimestamp.value,
        password: adminPassword.value // Persist password with same 8-hour expiry as auth state
      }
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData))
    } catch (error) {
      console.warn('Failed to save auth to storage:', error)
    }
  }

  const clearAuth = () => {
    isAuthenticated.value = false
    authTimestamp.value = null
    adminPassword.value = null // Clear password from memory
    // Don't clear lastError here - let it persist to show the error that caused the auth failure

    try {
      localStorage.removeItem(AUTH_STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear auth from storage:', error)
    }
  }

  const validatePassword = async (password) => {
    isLoading.value = true
    lastError.value = null

    try {
      if (!password || typeof password !== 'string') {
        throw new Error('Password is required')
      }
      const response = await fetch('/api/auth/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Authentication failed')
      }

      if (data.success) {
        // Authentication successful
        isAuthenticated.value = true
        authTimestamp.value = new Date().toISOString()
        adminPassword.value = password // Store password in memory for API calls
        saveAuthToStorage()
        return true
      } else {
        throw new Error('Invalid authentication response')
      }
    } catch (error) {
      lastError.value = error.message
      clearAuth()
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    clearAuth()
    adminPassword.value = null // Clear password from memory
    lastError.value = null // Clear errors on explicit logout
  }

  const extendSession = () => {
    if (isAuthenticated.value) {
      authTimestamp.value = new Date().toISOString()
      saveAuthToStorage()
    }
  }

  // Initialize from storage on store creation
  loadAuthFromStorage()

  return {
    // State (direct access for Pinia)
    isAuthenticated,
    authTimestamp,
    isLoading,
    lastError,
    adminPassword, // Expose password for API calls

    // Getters
    isAuthValid,
    needsAuthentication,
    timeUntilExpiry,

    // Actions
    validatePassword,
    logout,
    extendSession,
    loadAuthFromStorage,
    clearAuth
  }
})